import React, { useEffect, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { CLOUDINARY_CONFIG } from '../config/cloudinary';

const CloudinaryUpload = ({ onUpload, currentImages = [], maxImages = 5 }) => {
  const cloudinaryRef = useRef();
  const widgetRef = useRef();

  useEffect(() => {
    // Charger le script Cloudinary
    if (!window.cloudinary) {
      const script = document.createElement('script');
      script.src = 'https://upload-widget.cloudinary.com/global/all.js';
      script.async = true;
      script.onload = () => {
        cloudinaryRef.current = window.cloudinary;
      };
      document.body.appendChild(script);
    } else {
      cloudinaryRef.current = window.cloudinary;
    }
  }, []);

  const openUploadWidget = () => {
    if (!cloudinaryRef.current) {
      alert('Cloudinary is loading, please try again in a moment.');
      return;
    }

    widgetRef.current = cloudinaryRef.current.createUploadWidget(
      {
        cloudName: CLOUDINARY_CONFIG.cloudName,
        uploadPreset: CLOUDINARY_CONFIG.uploadPreset,
        sources: ['local', 'url', 'camera'],
        multiple: true,
        maxFiles: maxImages - currentImages.length,
        clientAllowedFormats: ['jpg', 'jpeg', 'png', 'webp', 'gif'],
        maxFileSize: 5000000, // 5 MB
        folder: 'products',
        resourceType: 'image',
        cropping: true,
        croppingAspectRatio: 1,
        croppingShowDimensions: true,
        croppingCoordinatesMode: 'custom',
        showSkipCropButton: true,
        styles: {
          palette: {
            window: '#FFFFFF',
            windowBorder: '#90A0B3',
            tabIcon: '#3b82f6',
            menuIcons: '#5A616A',
            textDark: '#000000',
            textLight: '#FFFFFF',
            link: '#3b82f6',
            action: '#3b82f6',
            inactiveTabIcon: '#9CA3AF',
            error: '#EF4444',
            inProgress: '#3b82f6',
            complete: '#10B981',
            sourceBg: '#F3F4F6'
          },
          fonts: {
            default: null,
            "'Poppins', sans-serif": {
              url: 'https://fonts.googleapis.com/css?family=Poppins',
              active: true
            }
          }
        },
        text: {
          en: {
            or: 'ou',
            back: 'Retour',
            close: 'Fermer',
            menu: {
              files: 'Mes fichiers',
              web: 'URL',
              camera: 'Appareil photo'
            },
            crop: {
              title: 'Recadrer',
              crop_btn: 'Recadrer et continuer',
              skip_btn: 'Ignorer',
              reset_btn: 'Réinitialiser'
            },
            local: {
              browse: 'Parcourir',
              dd_title_single: 'Glissez-déposez une image ici',
              dd_title_multi: 'Glissez-déposez des images ici',
              drop_title_single: 'Déposez le fichier pour l\'uploader',
              drop_title_multiple: 'Déposez les fichiers pour les uploader'
            }
          }
        }
      },
      (error, result) => {
        if (!error && result && result.event === 'success') {
          console.log('✅ Image uploadée:', result.info.secure_url);
          onUpload(result.info.secure_url);
        }
        if (error) {
          console.error('❌ Erreur upload Cloudinary:', error);
          alert('Erreur lors de l\'upload de l\'image. Veuillez réessayer.');
        }
      }
    );

    widgetRef.current.open();
  };

  const removeImage = (index) => {
    const newImages = currentImages.filter((_, i) => i !== index);
    onUpload(newImages);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Images du produit ({currentImages.length}/{maxImages})
        </label>
        {currentImages.length < maxImages && (
          <button
            type="button"
            onClick={openUploadWidget}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <Upload className="h-4 w-4 mr-2" />
            Ajouter une image
          </button>
        )}
      </div>

      {/* Prévisualisation des images */}
      {currentImages.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {currentImages.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt={`Produit ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 group-hover:border-blue-400 transition-colors"
              />
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                title="Supprimer cette image"
              >
                <X className="h-4 w-4" />
              </button>
              {index === 0 && (
                <span className="absolute bottom-1 left-1 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                  Principal
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">Aucune image ajoutée</p>
          <p className="mt-1 text-xs text-gray-500">
            Cliquez sur "Ajouter une image" pour commencer
          </p>
        </div>
      )}

      {currentImages.length > 0 && (
        <p className="text-xs text-gray-500">
          💡 La première image sera utilisée comme image principale
        </p>
      )}
    </div>
  );
};

export default CloudinaryUpload;

